/* Hero Section – Gold + Olive Design
 * Dark temple background, gold decorative accents, olive CTA
 * Annam portrait on the right, headline + CTA on the left
 */
import { IMAGES, STATS } from "@/lib/constants";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { ArrowDown } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

export default function HeroSection() {
  const { t } = useI18n();
  const { ref, isVisible } = useScrollAnimation(0.1);

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      ref={ref}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.heroBg}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.12_0.02_280/0.92)] via-[oklch(0.12_0.02_280/0.75)] to-[oklch(0.12_0.02_280/0.5)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[oklch(0.12_0.02_280/0.8)] via-transparent to-[oklch(0.12_0.02_280/0.4)]" />
      </div>

      {/* Mandala Overlay */}
      <div className="absolute inset-0 mandala-overlay pointer-events-none" />

      {/* Gold border frame (subtle) */}
      <div className="absolute inset-4 sm:inset-8 border border-[oklch(0.78_0.11_85/0.15)] rounded-lg pointer-events-none" />

      {/* Content */}
      <div className="container relative z-10 pt-28 pb-16 px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <div
            className={`transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[oklch(0.78_0.11_85/0.4)] bg-[oklch(0.78_0.11_85/0.08)] mb-6">
              <span className="w-2 h-2 rounded-full bg-[oklch(0.78_0.11_85)] animate-pulse" />
              <span className="text-sm text-[oklch(0.78_0.11_85)] tracking-wide">
                {t("landing.hero.badge")}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[oklch(0.95_0.01_80)] leading-[1.1] mb-6">
              {t("landing.hero.heading1")}{" "}
              <span className="text-[oklch(0.78_0.11_85)]">{t("landing.hero.heading2")}</span>
              <br />
              {t("landing.hero.heading3")}
            </h1>

            {/* Gold decorative line */}
            <div className="w-24 h-[2px] bg-gradient-to-r from-[oklch(0.78_0.11_85)] to-transparent mb-6" />

            <p
              className="text-lg sm:text-xl text-[oklch(0.75_0.02_80)] leading-relaxed mb-8 max-w-xl"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {t("landing.hero.subtitle")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href="/funnel"
                className="btn-olive px-6 py-4 rounded-full text-lg font-semibold tracking-wide text-center w-full sm:w-auto max-w-full"
              >
                {t("landing.hero.ctaStart")}
              </a>
              <a
                href="#was-ist-ayu"
                className="btn-gold-outline px-6 py-4 rounded-full text-center font-medium w-full sm:w-auto max-w-full"
              >
                {t("landing.hero.ctaLearnMore")}
              </a>
            </div>
          </div>

          {/* Right: Annam Portrait */}
          <div
            className={`relative transition-all duration-1000 delay-300 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            <div className="relative mx-auto max-w-md lg:max-w-lg">
              {/* Glow behind image */}
              <div className="absolute -inset-4 bg-[oklch(0.78_0.11_85/0.1)] rounded-3xl blur-3xl" />
              {/* Gold border frame */}
              <div className="relative rounded-2xl overflow-hidden border-2 border-[oklch(0.78_0.11_85/0.3)] shadow-2xl">
                <img
                  src={IMAGES.annamBlue}
                  alt="Annam Hanfstein – Gründerin von AYU"
                  className="w-full h-auto object-cover aspect-[3/4]"
                />
                {/* Gradient overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[oklch(0.12_0.02_280/0.8)] to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p
                    className="text-[oklch(0.95_0.01_80)] font-semibold text-lg"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {t("landing.hero.annamTitle")}
                  </p>
                  <p className="text-[oklch(0.78_0.11_85)] text-sm">
                    {t("landing.hero.annamRole")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div
          className={`mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 transition-all duration-1000 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {STATS.map((stat, i) => (
            <div
              key={i}
              className="text-center p-4 rounded-xl bg-[oklch(0.2_0.02_280/0.5)] backdrop-blur-sm border border-[oklch(0.78_0.11_85/0.1)]"
            >
              <div
                className="text-2xl sm:text-3xl font-bold text-[oklch(0.78_0.11_85)]"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {stat.value}
              </div>
              <div className="text-sm text-[oklch(0.7_0.02_80)] mt-1">
                {t(stat.labelKey as any)}
              </div>
            </div>
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ArrowDown className="text-[oklch(0.78_0.11_85/0.6)]" size={24} />
        </div>
      </div>
    </section>
  );
}
